import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Supplier } from "../../types";
import { supplierService } from "../../services/supplierService";
import { validateAndNormalizeBdPhone } from "../../utils/phone";

const schema = yup.object({
    name: yup.string().required("Supplier name is required"),
    contact_person: yup.string().optional(),
    email: yup.string().email("Invalid email format").optional(),
    phone: yup
        .string()
        .optional()
        .test(
            "bd-phone",
            "Phone number must start with 01 or +8801",
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    address: yup.string().optional(),
    website: yup.string().url("Invalid URL format").optional(),
    notes: yup.string().optional(),
});

interface SupplierFormData {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    website?: string;
    notes?: string;
}

interface SupplierFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (supplier: Supplier) => void;
    supplier?: Supplier | null;
    mode: "create" | "edit";
}

export const SupplierForm: React.FC<SupplierFormProps> = ({
    open,
    onClose,
    onSubmit,
    supplier,
    mode,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<SupplierFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            contact_person: "",
            email: "",
            phone: "",
            address: "",
            website: "",
            notes: "",
        },
    });

    useEffect(() => {
        if (open) {
            if (supplier && mode === "edit") {
                reset({
                    name: supplier.name,
                    contact_person: supplier.contact_person,
                    email: supplier.email,
                    phone: supplier.phone,
                    address: supplier.address,
                    website: supplier.website || "",
                    notes: supplier.notes || "",
                });
            } else {
                reset({
                    name: "",
                    contact_person: "",
                    email: "",
                    phone: "",
                    address: "",
                    website: "",
                    notes: "",
                });
            }
        }
    }, [open, supplier, mode, reset]);

    const handleFormSubmit = async (data: SupplierFormData) => {
        try {
            setLoading(true);
            setError("");

            // Remove empty strings for optional fields to avoid validation errors
            const cleanedData = {
                name: data.name,
                contact_person: data.contact_person?.trim() || undefined,
                email: data.email?.trim() || undefined,
                phone: data.phone
                    ? validateAndNormalizeBdPhone(data.phone).normalized
                    : undefined,
                address: data.address?.trim() || undefined,
                website: data.website?.trim() || undefined,
                notes: data.notes?.trim() || undefined,
            };

            let result: Supplier;
            if (mode === "create") {
                result = await supplierService.createSupplier(cleanedData);
            } else {
                result = await supplierService.updateSupplier(
                    supplier!.supplier_id,
                    cleanedData
                );
            }

            onSubmit(result);
            onClose();
        } catch (err: any) {
            setError(
                err.response?.data?.detail || `Failed to ${mode} supplier`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        setError("");
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {mode === "create" ? "Create New Supplier" : "Edit Supplier"}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                required
                                fullWidth
                                label="Supplier Name"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="contact_person"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Contact Person (Optional)"
                                error={!!errors.contact_person}
                                helperText={errors.contact_person?.message}
                            />
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Email (Optional)"
                                type="email"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />
                        )}
                    />

                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Phone (Optional)"
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                            />
                        )}
                    />

                    <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Address (Optional)"
                                multiline
                                rows={3}
                                error={!!errors.address}
                                helperText={errors.address?.message}
                            />
                        )}
                    />

                    <Controller
                        name="website"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Website (Optional)"
                                type="url"
                                error={!!errors.website}
                                helperText={errors.website?.message}
                            />
                        )}
                    />

                    <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Notes"
                                multiline
                                rows={2}
                                error={!!errors.notes}
                                helperText={errors.notes?.message}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : mode === "create" ? (
                        "Create"
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
